import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList,
  Legend,
} from "recharts";

import { NOMINAS_AJUSTES, trabajadoresConAjustes } from "./nominasAjustes";

const VISTA_CONTABLE = "contable";
const VISTA_AJUSTADO = "ajustado";
const VISTA_COMPARAR = "comparar";

const VISTA_OPTIONS = [
  { value: VISTA_CONTABLE, label: "Contable", color: "default" },
  { value: VISTA_AJUSTADO, label: "Ajustado", color: "primary" },
  { value: VISTA_COMPARAR, label: "Comparar", color: "info" },
];

const YAxisTick = ({ y, payload, fill }) => (
  <text x={2} y={y} dy={3} textAnchor="start" fill={fill} fontSize={10} fontWeight={500}>
    {payload?.value ?? ""}
  </text>
);

const fmtAxis = (v) => {
  if (typeof v !== "number" || Number.isNaN(v)) return v;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M€`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(abs >= 10_000 ? 0 : 1)} k€`;
  return `${Math.round(v)} €`;
};

const fmtBarLabel = (v) => {
  if (typeof v !== "number" || Number.isNaN(v) || v === 0) return "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(Math.round(v));
};

/** Escala rojo (mayor gasto) → verde (menor gasto). */
function colorBarra(rankIndex, totalWorkers, esOtros, theme) {
  if (esOtros) {
    return {
      fill: alpha(theme.palette.grey[500], theme.palette.mode === "dark" ? 0.5 : 0.42),
      stroke: alpha(theme.palette.grey[600], 0.3),
    };
  }

  const dark = theme.palette.mode === "dark";
  const ratio = totalWorkers > 1 ? rankIndex / (totalWorkers - 1) : 0;

  const hue = 6 + ratio * 128;
  const saturation = dark ? 76 - ratio * 6 : 80 - ratio * 8;
  const lightness = dark ? 54 + ratio * 5 : 44 + ratio * 10;

  return {
    fill: `hsla(${hue}, ${saturation}%, ${lightness}%, 0.92)`,
    stroke: `hsla(${hue}, ${saturation}%, ${Math.max(30, lightness - 12)}%, 0.45)`,
  };
}

const ChartTooltip = ({ active, payload, formatearImporte, theme, vista }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const pct = row?.pctDelTotal;
  const tieneAjuste = row?.tieneAjuste;
  const barValue = payload.find((p) => p.dataKey === row?.barKey)?.value ?? payload[0]?.value;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.6),
        bgcolor: alpha(theme.palette.background.paper, 0.96),
        backdropFilter: "blur(8px)",
        boxShadow: "0 12px 32px rgba(15,23,42,0.12)",
        maxWidth: 300,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.75 }}>
        {row?.nombreCompleto || row?.nombre}
      </Typography>
      {vista === VISTA_COMPARAR && tieneAjuste ? (
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>
              Contable
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatearImporte(row.totalContable ?? 0)}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>
              Ajustado
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: theme.palette.success.dark }}>
              {formatearImporte(row.totalAjustado ?? 0)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
            −{formatearImporte(row.descuentoTotal ?? 0)} atrasos
          </Typography>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: payload[0].fill }}>
            {formatearImporte(barValue ?? 0)}
          </Typography>
          {pct != null ? (
            <Typography variant="caption" color="text.secondary">
              ({pct.toFixed(1)}% del total)
            </Typography>
          ) : null}
        </Stack>
      )}
      {tieneAjuste && vista !== VISTA_COMPARAR ? (
        <Stack spacing={0.25} sx={{ mt: 1, pt: 1, borderTop: "1px dashed", borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            Contable: {formatearImporte(row.totalContable ?? 0)}
          </Typography>
          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
            −{formatearImporte(row.descuentoTotal ?? 0)} ({row.ajustesDetalle?.[0]?.label})
          </Typography>
        </Stack>
      ) : null}
    </Paper>
  );
};

const hideNumberSpinnersSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
};

const NominasCosteTrabajadorChart = ({
  trabajadores = [],
  otrosGrupo,
  year,
  formatearImporte,
  embedded = false,
  descuentoComisionesLydia,
  onDescuentoComisionesLydiaChange,
  guardandoAjuste = false,
  ocultarAjusteLydia = false,
}) => {
  const theme = useTheme();
  const ajusteLydia = NOMINAS_AJUSTES[0];

  const [vista, setVista] = useState(VISTA_AJUSTADO);
  const descuentoMensual = descuentoComisionesLydia ?? ajusteLydia.descuentoMensualDefault;
  const descuentosPorAjuste = useMemo(
    () => ({ [ajusteLydia.id]: descuentoMensual }),
    [ajusteLydia.id, descuentoMensual],
  );

  const trabajadoresAjustados = useMemo(
    () => trabajadoresConAjustes(trabajadores, NOMINAS_AJUSTES, descuentosPorAjuste),
    [trabajadores, descuentosPorAjuste],
  );

  const { chartData, totalGasto, workerCount, gapBorjaLydia } = useMemo(() => {
    const valorBarra = (t) => {
      if (ocultarAjusteLydia) return t.totalContable;
      if (vista === VISTA_CONTABLE) return t.totalContable;
      if (vista === VISTA_AJUSTADO) return t.totalAjustado;
      return t.totalAjustado;
    };

    const items = trabajadoresAjustados.map((t) => ({
      nombre: t.label,
      nombreCompleto: t.label,
      total: valorBarra(t),
      totalContable: t.totalContable,
      totalAjustado: t.totalAjustado,
      totalCompararContable: t.totalContable,
      totalCompararAjustado: t.totalAjustado,
      tieneAjuste: t.tieneAjuste,
      descuentoTotal: t.descuentoTotal,
      ajustesDetalle: t.ajustesDetalle,
      esOtros: false,
      barKey: vista === VISTA_COMPARAR ? "totalCompararAjustado" : "total",
    }));

    if (otrosGrupo?.subtotal?.total) {
      const ot = Number(otrosGrupo.subtotal.total) || 0;
      items.push({
        nombre: "Otros conceptos",
        nombreCompleto: otrosGrupo.label || "Otros conceptos",
        total: ot,
        totalContable: ot,
        totalAjustado: ot,
        totalCompararContable: ot,
        totalCompararAjustado: ot,
        tieneAjuste: false,
        descuentoTotal: 0,
        ajustesDetalle: [],
        esOtros: true,
        barKey: "total",
      });
    }

    const sortKey =
      vista === VISTA_CONTABLE
        ? "totalContable"
        : vista === VISTA_COMPARAR
          ? "totalAjustado"
          : "total";

    const sorted = items
      .filter((d) => (vista === VISTA_COMPARAR ? d.totalContable > 0 || d.totalAjustado > 0 : d.total > 0))
      .sort((a, b) => (b[sortKey] || b.total) - (a[sortKey] || a.total));

    const sum = sorted.reduce((s, d) => s + (d.total || 0), 0);
    const workerCount = sorted.filter((d) => !d.esOtros).length;
    let workerRank = 0;

    const borja = sorted.find((d) => /del rey/i.test(d.nombre) && !d.esOtros);
    const lydia = sorted.find((d) => /uralde/i.test(d.nombre) && !d.esOtros);
    const gapBorjaLydia =
      borja && lydia
        ? {
            contable: (borja.totalContable || 0) - (lydia.totalContable || 0),
            ajustado: (borja.totalAjustado || 0) - (lydia.totalAjustado || 0),
          }
        : null;

    return {
      chartData: sorted.map((d) => ({
        ...d,
        pctDelTotal: sum > 0 ? ((d.total || 0) / sum) * 100 : 0,
        workerRank: d.esOtros ? -1 : workerRank++,
      })),
      totalGasto: sum,
      workerCount,
      gapBorjaLydia,
    };
  }, [trabajadoresAjustados, otrosGrupo, vista, ocultarAjusteLydia]);

  if (!chartData.length) return null;

  const rowHeight = vista === VISTA_COMPARAR ? 34 : 26;
  const chartHeight = chartData.length * rowHeight + 12;
  const yAxisWidth = Math.min(
    embedded ? 260 : 220,
    Math.max(
      embedded ? 140 : 128,
      ...chartData.map((d) => String(d.nombre || "").length * 5.8),
    ),
  );
  const tickColor = theme.palette.text.secondary;
  const accent = theme.palette.primary.main;
  const gridColor = alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.35 : 0.55);
  const descuentoAnual = descuentoMensual * 12;

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: embedded ? 1.5 : 2 },
        mb: embedded ? 0 : 1.5,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.7),
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 45%)`
            : `linear-gradient(160deg, ${alpha("#eef4ff", 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 50%, ${alpha("#f4faf6", 0.5)} 100%)`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 28px rgba(0,0,0,0.22)"
            : "0 10px 40px rgba(15,23,42,0.06)",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupsOutlinedIcon sx={{ color: accent, fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: -0.2 }}>
              Coste por trabajador
            </Typography>
          </Stack>
          <Chip
            size="small"
            variant="outlined"
            label={`Coste A3 + extras (Borja incl. Revolut) · ${year}`}
            sx={{
              mt: 0.75,
              height: 22,
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: alpha(accent, 0.35),
              bgcolor: alpha(accent, 0.04),
            }}
          />
        </Box>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <Chip
            size="small"
            label={`${chartData.length} personas`}
            sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.08) }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Total ${formatearImporte(totalGasto)}`}
            sx={{ fontWeight: 700 }}
          />
          {gapBorjaLydia ? (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={
                vista === VISTA_CONTABLE
                  ? `Δ Borja−Lydia: ${formatearImporte(gapBorjaLydia.contable)}`
                  : `Δ Borja−Lydia: ${formatearImporte(gapBorjaLydia.ajustado)}`
              }
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Stack>
      </Stack>

      {!ocultarAjusteLydia ? (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.25, sm: 1.5 },
          mb: 1.5,
          borderRadius: 2.5,
          borderColor: alpha(theme.palette.warning.main, 0.35),
          bgcolor: alpha(theme.palette.warning.main, 0.04),
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: embedded ? "1fr" : "1fr auto",
              lg: "minmax(0, 1fr) auto",
            },
            gap: { xs: 1.25, md: 1.5 },
            alignItems: { md: "center" },
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.warning.main, 0.14),
                color: theme.palette.warning.dark,
              }}
            >
              <TuneOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                Ajuste visual · Lydia Uralde
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                Descontar atrasos en {ajusteLydia.cuenta} · compartido para todos · ejercicio {year}
                {guardandoAjuste ? " · guardando…" : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ justifyContent: { xs: "flex-start", lg: "flex-end" } }}
          >
            <TextField
              size="small"
              type="number"
              label="€ / mes"
              value={descuentoMensual}
              onChange={(e) => onDescuentoComisionesLydiaChange?.(e.target.value)}
              inputProps={{ min: 0, step: 1, inputMode: "decimal" }}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
              sx={{ width: 120, ...hideNumberSpinnersSx }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Anual −${formatearImporte(descuentoAnual)}`}
              sx={{ fontWeight: 700, height: 32 }}
            />
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {VISTA_OPTIONS.map((opt) => {
                const selected = vista === opt.value;
                const main =
                  opt.color === "default"
                    ? theme.palette.text.secondary
                    : theme.palette[opt.color].main;
                return (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    size="small"
                    clickable
                    onClick={() => setVista(opt.value)}
                    variant={selected ? "filled" : "outlined"}
                    color={opt.color === "default" ? undefined : opt.color}
                    sx={{
                      fontWeight: 700,
                      height: 28,
                      ...(selected
                        ? {}
                        : {
                            bgcolor: alpha(main, 0.06),
                            borderColor: alpha(main, 0.28),
                            color: main,
                          }),
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
        </Box>
      </Paper>
      ) : null}

      <Box
        sx={{
          width: "100%",
          maxHeight: embedded ? "min(520px, 50vh)" : "80vh",
          overflowY: "auto",
          overflowX: "hidden",
          borderRadius: 2.5,
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.4),
          pl: 0.25,
          pr: { xs: 0.5, sm: 1 },
          py: 0.75,
        }}
      >
        <Box sx={{ width: "100%", height: chartHeight, minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{
                top: 4,
                right: embedded ? 44 : 52,
                left: 0,
                bottom: vista === VISTA_COMPARAR ? 20 : 4,
              }}
              barCategoryGap={vista === VISTA_COMPARAR ? "18%" : "8%"}
              barGap={2}
            >
              <CartesianGrid
                strokeDasharray="4 6"
                horizontal={false}
                stroke={gridColor}
                vertical
              />
              <XAxis
                type="number"
                tickFormatter={fmtAxis}
                tick={{ fontSize: 11, fill: tickColor, fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
                domain={[0, (dataMax) => Math.ceil(dataMax * 1.08)]}
              />
              <YAxis
                type="category"
                dataKey="nombre"
                width={yAxisWidth}
                interval={0}
                tick={<YAxisTick fill={tickColor} />}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={
                  <ChartTooltip
                    formatearImporte={formatearImporte}
                    theme={theme}
                    vista={vista}
                  />
                }
                cursor={{
                  fill: alpha(theme.palette.primary.main, 0.05),
                  radius: 8,
                }}
                animationDuration={200}
              />
              {vista === VISTA_COMPARAR ? (
                <>
                  <Legend
                    verticalAlign="bottom"
                    height={16}
                    iconSize={8}
                    wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                  />
                  <Bar
                    dataKey="totalCompararContable"
                    name="Contable"
                    radius={[0, 4, 4, 0]}
                    maxBarSize={10}
                    fill={alpha(theme.palette.grey[500], 0.55)}
                    isAnimationActive
                    animationDuration={500}
                  >
                    <LabelList
                      dataKey="totalCompararContable"
                      position="right"
                      formatter={fmtBarLabel}
                      style={{ fontSize: 9, fontWeight: 600, fill: tickColor }}
                    />
                  </Bar>
                  <Bar
                    dataKey="totalCompararAjustado"
                    name="Ajustado"
                    radius={[0, 4, 4, 0]}
                    maxBarSize={10}
                    isAnimationActive
                    animationDuration={500}
                  >
                    {chartData.map((entry) => {
                      const { fill, stroke } = colorBarra(
                        entry.workerRank,
                        workerCount,
                        entry.esOtros,
                        theme,
                      );
                      return (
                        <Cell
                          key={`adj-${entry.nombreCompleto}`}
                          fill={fill}
                          stroke={stroke}
                          strokeWidth={1}
                        />
                      );
                    })}
                    <LabelList
                      dataKey="totalCompararAjustado"
                      position="right"
                      formatter={fmtBarLabel}
                      style={{ fontSize: 9, fontWeight: 700, fill: tickColor }}
                    />
                  </Bar>
                </>
              ) : (
                <Bar
                  dataKey="total"
                  name={vista === VISTA_AJUSTADO ? "Coste ajustado" : "Coste contable"}
                  radius={[0, 6, 6, 0]}
                  maxBarSize={14}
                  isAnimationActive
                  animationDuration={700}
                  animationEasing="ease-out"
                >
                  {chartData.map((entry) => {
                    const { fill, stroke } = colorBarra(
                      entry.workerRank,
                      workerCount,
                      entry.esOtros,
                      theme,
                    );
                    return (
                      <Cell
                        key={entry.nombreCompleto}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={1}
                      />
                    );
                  })}
                  <LabelList
                    dataKey="total"
                    position="right"
                    formatter={fmtBarLabel}
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      fill: tickColor,
                    }}
                  />
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
};

export default NominasCosteTrabajadorChart;
